import { IndexAggregation } from "../../core/api/contracts/PipelineOperation";
import { UField } from "../../core/api/contracts/UField";

export type NotebookMode = "sql" | "columns";
export type NotebookViews = "view" | "properties";
export interface NotebookRouteParams {
  mode: NotebookMode;
  tab: NotebookViews;
}
export interface URIProvider {
  readonly edit: string;
  readonly postDeploy: string;
  readonly list: string;

  view(tab?: string, at?: string): string;

  notebook(mode: NotebookMode, tab?: NotebookViews): string;

  createAggregation(type: string): string;

  editAggregation(aggregation: IndexAggregation): string;

  editLookup(lookupClazz: string, field: UField): string;

  createCustomField(family: string, filterType?: string, kind?: string): string;

  createUnmappedCustomField(family: string, filter?: string, kind?: string): string;

  createInlineCustomField(family: string, filter?: string, kind?: string): string;

  editCustomField(field: UField): string;

  deploy(tab?: string): string;

  clone(tab?: string, postAction?: string): string;

  deletePath(tab?: string): string;

  stop(tab?: string): string;

  archive(tab?: string): string;

  dashboard(tab?: string): string;

  selectAggregation(tab?: string): string;

  abortDelete(tab?: string): string;
}

const defaults = {
  DEFAULT_TAB: "schema",
  CREATE_AGGREGATION: "aggregations/create/aggregation",
  EDIT_AGGREGATION: "aggregations/edit",
  CREATE_CUSTOM_FIELD: "customField",
};

type TemplateURIProviderParams = {
  baseUri: string;
  postDeployTab: string;
  defaultTab?: string;
  createAggregationSuffix?: string;
  list: string;
  createCustomFieldSuffix?: string;
  createViewPostfix?: (tab?: string, at?: string) => string;
  dashboard?: string;
};

class TemplateURIProviderBase implements URIProvider {
  _editUri: string;
  _view: string;
  _dashboard: string;
  _postDeploy: string;
  _createAggregation: string;
  _list: string;
  _createCustomField: string;
  _createUnamppedCustomField: string;
  _createInlineCustomField: string;
  _baseUri: string;
  _createViewPostfix: (tab?: string, at?: string) => string;

  constructor(params: TemplateURIProviderParams) {
    const {
      baseUri,
      postDeployTab,
      createAggregationSuffix,
      createCustomFieldSuffix,
      createViewPostfix,
      dashboard,
    } = params;
    this._createViewPostfix = createViewPostfix || (() => "/");
    this._baseUri = baseUri;
    this._editUri = `${baseUri}/edit`;
    this._view = `${baseUri}/view`;
    this._dashboard = `${baseUri}/${dashboard || "dashboard"}`;
    this._postDeploy = `${this._view}/${postDeployTab}`;
    this._createAggregation = `${this._editUri}/${createAggregationSuffix || defaults.CREATE_AGGREGATION}`;
    this._createCustomField = `${this._editUri}/${createCustomFieldSuffix || defaults.CREATE_CUSTOM_FIELD}`;
    this._createUnamppedCustomField = `${this._editUri}/unmapped${
      createCustomFieldSuffix || defaults.CREATE_CUSTOM_FIELD
    }`;
    this._createInlineCustomField = `${this._editUri}/inline${createCustomFieldSuffix || defaults.CREATE_CUSTOM_FIELD}`;
    this._list = params.list;
  }

  get edit(): string {
    return this._editUri;
  }

  get postDeploy(): string {
    return this._postDeploy;
  }

  view(tab?: string, at?: string): string {
    return `${this._view}${this._createViewPostfix(tab, at)}`;
  }

  _action(currentTab: string | null, action: string): string {
    return `${this._view}/${currentTab || defaults.DEFAULT_TAB}/${action}`;
  }

  deploy(tab?: string): string {
    return this._action(tab, "deploy");
  }

  clone(tab?: string, postAction: string | null = null): string {
    return `${this._action(tab, "clone")}/${postAction ? postAction : ""}`;
  }

  deletePath(tab?: string): string {
    return this._action(tab, "delete");
  }

  archive(tab?: string): string {
    return this._action(tab, "archive");
  }

  stop(tab?: string): string {
    return this._action(tab, "stop");
  }

  get list(): string {
    return this._list;
  }

  createAggregation(type: string, field?: string) {
    return `${this._createAggregation}/${encodeURIComponent(type)}`;
  }

  createCustomField(family: string, filterType?: string, kind?: string) {
    return `${this._createCustomField}/${family}${kind ? `/${kind}/` : "/"}${filterType ? filterType : ""}`;
  }

  createUnmappedCustomField(family: string, filterType?: string, kind?: string) {
    return `${this._createUnamppedCustomField}/${family}${kind ? `/${kind}/` : "/"}${filterType ? filterType : ""}`;
  }

  createInlineCustomField(family: string, filterType?: string, kind?: string) {
    return `${this._createInlineCustomField}/${family}${kind ? `/${kind}/` : "/"}${filterType ? filterType : ""}`;
  }

  dashboard(tab: string = ""): string {
    return `${this._dashboard}/${tab}`;
  }

  editCustomField(field: UField): string {
    return `${this._editUri}/customField/edit/${encodeURIComponent(UField.key(field))}`;
  }

  editAggregation(aggregation: IndexAggregation): string {
    return `${this._editUri}/${defaults.EDIT_AGGREGATION}/${encodeURIComponent(aggregation.name)}`;
  }

  editLookup(lookupClazz: string, field: UField): string {
    return `${this._editUri}/${lookupClazz}/edit/${encodeURIComponent(UField.key(field))}`;
  }

  selectAggregation(tab?: string): string {
    return this._action(tab, "aggregations/select");
  }

  abortDelete(tab?: string): string {
    return this._action(tab || "information", "abortDelete");
  }

  notebook(mode: NotebookMode, tab: "properties" | "view" = "view"): string {
    return `${this._baseUri}/notebook/${mode}/${tab}`;
  }
}

export class OutputTemplateURIProvider extends TemplateURIProviderBase {
  constructor(id: string, parametersClazz: string) {
    const baseWord = parametersClazz === "DashboardOutputParameters" ? "dashboards" : "outputs";
    super({
      baseUri: `/${baseWord}/${id}/${parametersClazz}`,
      postDeployTab: "monitoring",
      list: `/${baseWord}/list`,
      createViewPostfix: (tab, at) => `${tab ? "/" + tab : ""}${at ? "?column=" + encodeURIComponent(at) : "/"}`,
      dashboard: "view",
    });
  }
}

export class IndexTemplateURIProvider extends TemplateURIProviderBase {
  constructor(id: string) {
    const postDeployTab = "monitoring";
    super({
      baseUri: `/indexes/IndexTemplate/${id}`,
      postDeployTab: postDeployTab,
      list: "/indexes/list",
      createViewPostfix: (tab) => `${tab ? "/" + tab : "/"}`,
    });
    this._postDeploy = `${this._dashboard}/${postDeployTab}`;
  }
}

const providerFactories: any = {
  IndexTemplate: (t: any) => new IndexTemplateURIProvider(t.id),
  OutputTemplate: (t: any) => new OutputTemplateURIProvider(t.id, t.outputParameters && t.outputParameters.clazz),
};

export const createURIPRovider = (x: { clazz: string; id?: string }): URIProvider => providerFactories[x.clazz](x);
